import { useMemo } from "react"
import {
    APIGroundTruthModel,
    APIGroundTruthDetailedModel
} from "../../../../../../../../Models/API/APIGroundTruthModel"
import { useDispatch } from "react-redux"
import {
    InputOutputData,
    Button,
    CardStat,
    ModalPanel,
    Title
} from "dash-styled"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../../../Routes"
import {
    hasResult,
    FetchState,
    getValue
} from "../../../../../../../../Models/Client/FetchState"
import { SimpleInfoBox } from "../../../../../../../Common/SimpleInfoBox/SimpleInfoBox"
import { useGroundTruth } from "../../../../../../../../Hooks/useGroundTruth"
import { FilterAndSortExpression } from "../../../../../../../../Models/Client/FilterAndSortExpression"
import { useGroundTruthListNavigation } from "../../../../../../../../Hooks/useGroundTruthListNavigation"
import { findFirstError } from "../../../../../../../../Models/Utility/Result"
import { SortExpression } from "../../../../../../../../Models/Client/SortExpression"
import { groundTruthSortOptions } from "../../../Common/constants"
import { ModalPanelNavHeader } from "../../../../../../../Common/ModalPanelNavHeader/ModalPanelNavHeader"
import { Breadcrumbs } from "../../../../../Common/Breadcrumbs/Breadcrumbs"
import {
    APIAppInstanceModel,
    isAppInstanceEnabled
} from "../../../../../../../../Models/API/AppInstanceModel"
import { useAppInstance } from "../../../../../../../../Hooks/useAppInstance"
import { LoadingContainer } from "../../../../../../../Common/LoadingContainer/LoadingContainer"
import { isQualityValue } from "../../../../../../../../Models/DataProgram/Measurement"
import { DataAccessContext } from "../../../../../../../../Context/DataAccessContext"
import { DataAccess } from "../../../../../../../../Models/Client/DataAccess"
import { useErrorTrackingInfo } from "../../../../../../../../Hooks/useErrorTrackingInfo"
import { useAppSchema } from "../../../../../../../../Hooks/useAppSchema"
import { InputFormRender } from "../../../../../../../Common/InputFormRender/InputFormRender"

interface BaselineDataDetailProps {
    orgUsername: string
    appID: string
    groundTruthID?: string
    filterAndSort: FilterAndSortExpression<APIGroundTruthModel>
}

interface BaselineDataDetailIsolatedProps {
    appID: string
    groundTruthID?: string
    groundTruthFetch: FetchState<APIGroundTruthDetailedModel>
    instanceFetch: FetchState<APIAppInstanceModel>
    filterAndSort: FilterAndSortExpression<APIGroundTruthModel>
    cursor: { foundAt: number; totalCount: number } | undefined
    onPrev?: () => void
    onNext?: () => void
    onClose: () => void
    onEdit: () => void
    onChangeSort: (sort: SortExpression<APIGroundTruthModel>) => void
}

function BaselineDataDetailIsolated({
    appID,
    groundTruthID,
    groundTruthFetch,
    instanceFetch,
    filterAndSort,
    cursor,
    onClose,
    onNext,
    onPrev,
    onEdit,
    onChangeSort
}: BaselineDataDetailIsolatedProps) {
    const schemaFetch = useAppSchema(appID, false)
    const renderBody = () => {
        if (!hasResult(groundTruthFetch) || !hasResult(schemaFetch)) {
            return undefined
        }

        if (groundTruthFetch.isError || schemaFetch.isError) {
            return (
                <SimpleInfoBox variant="alert">
                    Ground truth data '{groundTruthID || ""}' is not available.{" "}
                    {findFirstError(groundTruthFetch, schemaFetch)}
                </SimpleInfoBox>
            )
        }

        const groundTruth = groundTruthFetch.value

        return (
            <>
                <div className="w-0 flex-shrink flex-grow p-6 border-t-0 border-r border-b-0 border-l-0 border-solid border-gray-300">
                    <div className="pb-4">
                        <Breadcrumbs
                            elements={[
                                "Projects",
                                getValue(instanceFetch)?.name || "",
                                "Ground-truth Data"
                            ]}
                        />
                        <Title
                            component="h2"
                            variant="heading1"
                            weight="semibold"
                            className="flex-shrink-0"
                            noMargin={true}
                        >
                            Data point {groundTruth.id}
                        </Title>
                    </div>
                    <InputOutputData
                        className="pb-4"
                        inputs={
                            <InputFormRender
                                {...schemaFetch.value.input}
                                formData={groundTruth.input}
                                skipMissingDataField={true}
                            />
                        }
                        outputs={
                            <InputFormRender
                                {...schemaFetch.value.output}
                                formData={groundTruth.label}
                                skipMissingDataField={true}
                            />
                        }
                    />
                    <div className="flex">
                        <CardStat
                            value={groundTruth.used.toString()}
                            label="uses"
                        />
                        {Object.entries(groundTruth?.baselineQuality ?? {}).map(
                            ([qualityName, value]) =>
                                isQualityValue(value) ? (
                                    <CardStat
                                        className="ml-4"
                                        value={value.value.toPrecision(2)}
                                        label={value["ui:name"] ?? qualityName}
                                    />
                                ) : null
                        )}
                    </div>
                </div>
                {isAppInstanceEnabled(getValue(instanceFetch)) && (
                    <div
                        className="flex flex-col justify-between p-6 pt-12 flex-grow-0 flex-shrink-0"
                        style={{ width: "300px" }}
                    >
                        <Button
                            color="plain"
                            variant="contained"
                            onClick={onEdit}
                            testId="ground-truth-edit-button"
                        >
                            Edit Ground Truth Data
                        </Button>
                    </div>
                )}
            </>
        )
    }

    return (
        <ModalPanel
            onClose={onClose}
            testId="groundtruth-detail-modal"
            className="p-0 flex mt-24 w-full md:w-11/12 xl:w-5/6 relative text-sm"
            position="top"
            scrimOpacity="darker"
            noPadding={true}
        >
            <ModalPanelNavHeader
                mode="all"
                sort={{
                    applied: filterAndSort.sort,
                    fields: groundTruthSortOptions,
                    onChange: onChangeSort
                }}
                cursor={cursor}
                onPrev={onPrev}
                onNext={onNext}
                containerClassName="-mt-24"
            />
            <LoadingContainer
                defaultSize={{ height: 480 }}
                className="flex w-full"
            >
                {renderBody()}
            </LoadingContainer>
        </ModalPanel>
    )
}

export function BaselineDataDetail({
    orgUsername,
    appID,
    groundTruthID,
    filterAndSort
}: BaselineDataDetailProps) {
    useErrorTrackingInfo("superai.groundTruthID", groundTruthID)

    const dispatch = useDispatch()
    const instanceFetch = useAppInstance(appID)
    const groundTruthFetch = useGroundTruth(appID, groundTruthID)
    const handleCloseModal = () =>
        dispatch(
            push(
                routePaths.appCustomizeBaselineData(
                    orgUsername,
                    appID,
                    filterAndSort
                )
            )
        )
    const navigation = useGroundTruthListNavigation(
        orgUsername,
        appID,
        filterAndSort,
        groundTruthID
    )
    const handleEdit = () => {
        if (groundTruthID === undefined) {
            return
        }

        dispatch(
            push(
                routePaths.appCustomizeBaselineDataEdit(
                    orgUsername,
                    appID,
                    groundTruthID,
                    filterAndSort
                )
            )
        )
    }
    const handleChangeSort = (sort: SortExpression<APIGroundTruthModel>) =>
        dispatch(
            push(
                routePaths.appCustomizeBaselineData(
                    orgUsername,
                    appID,
                    {
                        ...filterAndSort,
                        sort
                    },
                    true
                )
            )
        )

    const dataAccess = useMemo(
        () =>
            groundTruthID ? DataAccess.perGT(groundTruthID) : DataAccess.owner,
        [groundTruthID]
    )

    return (
        <DataAccessContext.Provider value={dataAccess}>
            <BaselineDataDetailIsolated
                appID={appID}
                groundTruthID={groundTruthID}
                groundTruthFetch={groundTruthFetch}
                instanceFetch={instanceFetch}
                filterAndSort={filterAndSort}
                cursor={navigation?.cursor}
                onClose={handleCloseModal}
                onNext={navigation?.onNext}
                onPrev={navigation?.onPrev}
                onEdit={handleEdit}
                onChangeSort={handleChangeSort}
            />
        </DataAccessContext.Provider>
    )
}
